import { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { adminApi, ASSETS, formatApiError } from "@/lib/api";
import {
  Calendar as CalendarIcon,
  Clock,
  User as UserIcon,
  LogOut,
  Loader2,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  Ban,
  Link2,
  DollarSign,
  Copy,
  Download,
  Share2,
  Trophy,
  Medal,
  Crown,
  TrendingUp,
  Sparkles,
  X as CloseX,
  Swords,
  Star,
  ScanLine,
} from "lucide-react";
import { format, addDays, startOfWeek } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { StaffContests } from "@/components/StaffContests";
import { MyReviews } from "@/components/MyReviews";
import { HowItWorks } from "@/components/HowItWorks";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const TABS = [
  { id: "schedule", label: "My Schedule", icon: CalendarIcon },
  { id: "availability", label: "Availability", icon: Clock },
  { id: "timeoff", label: "Time Off", icon: Ban },
  { id: "referrals", label: "My Link", icon: Link2 },
  { id: "contests", label: "Contests", icon: Swords },
  { id: "leaderboard", label: "Leaderboard", icon: Trophy },
  { id: "reviews", label: "My Reviews", icon: Star },
  { id: "profile", label: "Profile", icon: UserIcon },
];

export default function StaffPortal() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("schedule");

  if (loading) return null;
  if (!user) return <Navigate to="/staff/login" replace />;
  if (user.role === "owner" || user.role === "admin") return <Navigate to="/admin" replace />;

  function handleLogout() {
    logout();
    navigate("/staff/login");
  }

  return (
    <div data-testid="staff-portal" className="min-h-screen bg-[var(--trio-bg)]">
      <header
        className="sticky top-0 z-40 bg-[var(--trio-text)] text-[var(--trio-bg)]"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={ASSETS.logo} alt="Trio" className="h-8 w-auto invert" />
            <div>
              <div className="text-[9px] tracking-[0.3em] uppercase text-[var(--trio-accent)] font-bold">
                Staff Portal
              </div>
              <div className="text-sm font-medium">{user.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
          <Link
            to="/door"
            data-testid="staff-door-scanner"
            className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-3 py-2 text-[11px] tracking-widest uppercase font-bold"
          >
            <ScanLine className="w-4 h-4" /> Door Scanner
          </Link>
          <button
            onClick={handleLogout}
            data-testid="staff-logout"
            className="inline-flex items-center gap-2 text-xs tracking-widest uppercase hover:text-[var(--trio-accent)]"
          >
            <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign Out</span>
          </button>
          </div>
        </div>
        <nav className="max-w-4xl mx-auto px-4 sm:px-6 flex overflow-x-auto border-t border-[var(--trio-bg)]/15">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`staff-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-4 py-3 text-xs tracking-widest uppercase font-bold border-b-2 whitespace-nowrap ${
                  tab === t.id
                    ? "border-[var(--trio-primary)] text-[var(--trio-accent)]"
                    : "border-transparent text-[var(--trio-bg)]/60 hover:text-[var(--trio-bg)]"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <DailySummaryBanner />
        {tab === "schedule" && <MySchedule />}
        {tab === "availability" && <MyAvailability />}
        {tab === "timeoff" && <MyTimeOff />}
        {tab === "referrals" && <MyReferrals />}
        {tab === "contests" && <StaffContests />}
        {tab === "leaderboard" && <Leaderboard />}
        {tab === "reviews" && <MyReviews />}
        {tab === "profile" && <MyProfile />}
      </main>
    </div>
  );
}

// ---------- My Schedule ----------
function MySchedule() {
  const [shifts, setShifts] = useState(null);
  const [weekStart, setWeekStart] = useState(() =>
    format(startOfWeek(new Date(), { weekStartsOn: 0 }), "yyyy-MM-dd")
  );

  useEffect(() => {
    adminApi
      .get(`/staff/shifts?week_start=${weekStart}`)
      .then((r) => setShifts(r.data))
      .catch(() => setShifts([]));
  }, [weekStart]);

  const days = Array.from({ length: 7 }, (_, i) =>
    format(addDays(new Date(weekStart + "T00:00:00"), i), "yyyy-MM-dd")
  );

  function shiftWeek(delta) {
    const d = new Date(weekStart + "T00:00:00");
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(format(d, "yyyy-MM-dd"));
  }

  return (
    <section data-testid="staff-schedule" className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display uppercase text-3xl tracking-tight">My Schedule</h2>
        <div className="flex gap-2">
          <button
            onClick={() => shiftWeek(-1)}
            className="px-3 py-1.5 text-xs tracking-widest uppercase border border-[var(--trio-border)] hover:border-[var(--trio-text)]"
          >
            ← Prev
          </button>
          <button
            onClick={() => shiftWeek(1)}
            className="px-3 py-1.5 text-xs tracking-widest uppercase border border-[var(--trio-border)] hover:border-[var(--trio-text)]"
          >
            Next →
          </button>
        </div>
      </div>
      <div className="text-xs text-[var(--trio-muted)] uppercase tracking-widest">
        Week of {format(new Date(weekStart + "T00:00:00"), "MMM d, yyyy")}
      </div>

      {shifts === null ? (
        <div className="flex items-center gap-2 text-sm text-[var(--trio-muted)]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : shifts.length === 0 ? (
        <div className="border border-dashed border-[var(--trio-border)] p-10 text-center text-sm text-[var(--trio-muted)]">
          No shifts published for this week yet.
        </div>
      ) : (
        <div className="space-y-2">
          {days.map((d) => {
            const dayShifts = shifts.filter((s) => s.date === d);
            if (dayShifts.length === 0) return null;
            return (
              <div key={d} className="border border-[var(--trio-border)] bg-[var(--trio-surface)] p-4">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold mb-2">
                  {format(new Date(d + "T00:00:00"), "EEEE, MMM d")}
                </div>
                {dayShifts.map((s) => (
                  <div key={s.id} className="flex items-center justify-between py-1.5" data-testid={`staff-shift-${s.id}`}>
                    <div>
                      <div className="font-medium">
                        {s.start_time} – {s.end_time}
                      </div>
                      {s.role && <div className="text-xs text-[var(--trio-muted)]">{s.role}</div>}
                    </div>
                    {s.notes && <div className="text-xs text-[var(--trio-muted)] italic">{s.notes}</div>}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ---------- My Availability ----------
function MyAvailability() {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      const { data } = await adminApi.get("/staff/availability");
      const byDay = new Map(data.map((r) => [r.day_of_week, r]));
      setRows(
        Array.from({ length: 7 }, (_, i) =>
          byDay.get(i) || {
            day_of_week: i,
            enabled: false,
            start_time: "16:00",
            end_time: "23:00",
          }
        )
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  function update(i, patch) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  async function save() {
    setSaving(true);
    try {
      await adminApi.put("/staff/availability", { items: rows });
      toast.success("Availability saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section data-testid="staff-availability" className="space-y-4">
      <div>
        <h2 className="font-display uppercase text-3xl tracking-tight">Weekly Availability</h2>
        <p className="text-sm text-[var(--trio-muted)] mt-1">
          Days and time windows you can regularly work. The schedule builder won't book
          you outside these hours or on days marked off.
        </p>
      </div>
      {rows === null ? (
        <div className="flex items-center gap-2 text-sm text-[var(--trio-muted)]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div
              key={i}
              data-testid={`avail-row-${i}`}
              className={`border p-3 flex items-center gap-3 ${
                r.enabled ? "border-[var(--trio-border)] bg-[var(--trio-surface)]" : "border-[var(--trio-border)]/50 opacity-60"
              }`}
            >
              <label className="flex items-center gap-2 min-w-[110px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={r.enabled}
                  onChange={(e) => update(i, { enabled: e.target.checked })}
                  data-testid={`avail-toggle-${i}`}
                  className="w-4 h-4"
                />
                <span className="text-sm font-bold tracking-widest uppercase">{DAYS[i]}</span>
              </label>
              <input
                type="time"
                value={r.start_time}
                disabled={!r.enabled}
                onChange={(e) => update(i, { start_time: e.target.value })}
                data-testid={`avail-start-${i}`}
                className="bg-[var(--trio-bg)] border border-[var(--trio-border)] px-2 py-1 text-sm disabled:opacity-40"
              />
              <span className="text-[var(--trio-muted)]">–</span>
              <input
                type="time"
                value={r.end_time}
                disabled={!r.enabled}
                onChange={(e) => update(i, { end_time: e.target.value })}
                data-testid={`avail-end-${i}`}
                className="bg-[var(--trio-bg)] border border-[var(--trio-border)] px-2 py-1 text-sm disabled:opacity-40"
              />
            </div>
          ))}
          <button
            onClick={save}
            disabled={saving}
            data-testid="avail-save"
            className="mt-4 inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-6 py-3 text-xs tracking-widest uppercase font-bold disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save Availability
          </button>
        </div>
      )}
    </section>
  );
}

// ---------- Time Off ----------
function MyTimeOff() {
  const [rows, setRows] = useState(null);
  const [date, setDate] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      const { data } = await adminApi.get("/staff/time-off");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  async function submit(e) {
    e.preventDefault();
    if (!date) return toast.error("Pick a date");
    setSaving(true);
    try {
      const { data } = await adminApi.post("/staff/time-off", {
        date: format(date, "yyyy-MM-dd"),
        reason,
      });
      setRows((prev) => [data, ...(prev || [])]);
      setDate(null);
      setReason("");
      toast.success("Time-off requested");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  async function cancel(tid) {
    if (!confirm("Cancel this request?")) return;
    try {
      await adminApi.delete(`/staff/time-off/${tid}`);
      setRows((prev) => prev.filter((r) => r.id !== tid));
      toast.success("Cancelled");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <section data-testid="staff-timeoff" className="space-y-6">
      <h2 className="font-display uppercase text-3xl tracking-tight">Time Off</h2>

      <form onSubmit={submit} className="border border-[var(--trio-border)] bg-[var(--trio-surface)] p-4 space-y-3">
        <div className="text-xs tracking-widest uppercase text-[var(--trio-primary)] font-bold">
          Request a day off
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="timeoff-date-trigger"
                className="flex-1 flex items-center gap-2 border border-[var(--trio-border)] bg-[var(--trio-bg)] px-3 py-2 text-left text-sm"
              >
                <CalendarIcon className="w-4 h-4 text-[var(--trio-primary)]" />
                {date ? format(date, "EEE, MMM d, yyyy") : <span className="text-[var(--trio-muted)]">Pick a date</span>}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="p-0 bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional)"
            data-testid="timeoff-reason"
            className="flex-1 bg-[var(--trio-bg)] border border-[var(--trio-border)] px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={saving}
            data-testid="timeoff-submit"
            className="bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-5 py-2 text-xs tracking-widest uppercase font-bold disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 inline mr-1" />Request</>}
          </button>
        </div>
      </form>

      {rows === null ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : rows.length === 0 ? (
        <div className="text-sm text-[var(--trio-muted)] italic">No time-off requests yet.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div
              key={r.id}
              data-testid={`timeoff-row-${r.id}`}
              className="flex items-center gap-3 border border-[var(--trio-border)] bg-[var(--trio-surface)] p-3"
            >
              <StatusBadge status={r.status} />
              <div className="flex-1">
                <div className="text-sm font-medium">
                  {format(new Date(r.date + "T00:00:00"), "EEE, MMM d, yyyy")}
                </div>
                {r.reason && <div className="text-xs text-[var(--trio-muted)]">{r.reason}</div>}
              </div>
              {r.status === "pending" && (
                <button
                  onClick={() => cancel(r.id)}
                  data-testid={`timeoff-cancel-${r.id}`}
                  className="p-2 text-[var(--trio-muted)] hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { icon: Clock, cls: "text-[var(--trio-muted)] border-[var(--trio-border)]", label: "Pending" },
    approved: { icon: CheckCircle2, cls: "text-green-600 border-green-500/40", label: "Approved" },
    denied: { icon: XCircle, cls: "text-red-500 border-red-500/40", label: "Denied" },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <div className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-[10px] tracking-widest uppercase font-bold ${s.cls}`}>
      <Icon className="w-3 h-3" />
      {s.label}
    </div>
  );
}

// ---------- Profile ----------
function MyProfile() {
  const { user } = useAuth();
  const [phone, setPhone] = useState(user?.phone || "");
  const [saving, setSaving] = useState(false);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.patch("/staff/me", { phone });
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section data-testid="staff-profile" className="space-y-4 max-w-md">
      <h2 className="font-display uppercase text-3xl tracking-tight">Profile</h2>
      <form onSubmit={save} className="space-y-4">
        <ReadOnlyField label="Name" value={user.name} />
        <ReadOnlyField label="Email" value={user.email} />
        <ReadOnlyField label="Position" value={user.position || "—"} />
        <div>
          <label className="text-xs tracking-widest uppercase">Phone</label>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            data-testid="profile-phone"
            className="mt-2 w-full bg-[var(--trio-bg)] border border-[var(--trio-border)] px-3 py-2 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="profile-save"
          className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-5 py-2 text-xs tracking-widest uppercase font-bold disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Save
        </button>
      </form>
      <p className="text-xs text-[var(--trio-muted)]">
        To change your name, email, or position, message the owner.
      </p>
    </section>
  );
}

function ReadOnlyField({ label, value }) {
  return (
    <div>
      <label className="text-xs tracking-widest uppercase text-[var(--trio-muted)]">{label}</label>
      <div className="mt-1 text-sm font-medium">{value}</div>
    </div>
  );
}


// ---------- My Referrals (link + QR + commissions) ----------
function MyReferrals() {
  const [refData, setRefData] = useState(null);
  const [comms, setComms] = useState(null);

  useEffect(() => {
    adminApi.get("/staff/referral").then((r) => setRefData(r.data)).catch(() => setRefData(null));
    adminApi.get("/staff/commissions").then((r) => setComms(r.data)).catch(() => setComms(null));
  }, []);

  if (!refData) {
    return (
      <section className="flex items-center gap-2 text-sm text-[var(--trio-muted)]" data-testid="staff-referrals-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your link…
      </section>
    );
  }

  const origin = window.location.origin;
  const link = `${origin}/r/${refData.referral_code}`;
  const bookingLink = `${origin}/?ref=${refData.referral_code}`;
  const eventsLink = `${origin}/events?ref=${refData.referral_code}`;

  function copy(text, label) {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Couldn't copy")
    );
  }

  function downloadQR() {
    const canvas = document.querySelector('[data-testid="staff-qr"] canvas');
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `trio-${refData.referral_code}-qr.png`;
    a.click();
    toast.success("QR code downloaded");
  }

  async function shareLink() {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Book a table at Trio",
          text: "Come eat, drink, and vibe with me at Trio.",
          url: link,
        });
      } catch (_) {
        /* user cancelled */
      }
    } else {
      copy(link, "Link");
    }
  }

  const totalPending = (comms?.totals?.reservations?.pending || 0) + (comms?.totals?.tickets?.pending || 0);
  const totalEarned = (comms?.totals?.reservations?.earned || 0) + (comms?.totals?.tickets?.earned || 0);
  const totalPaid = (comms?.totals?.reservations?.paid || 0) + (comms?.totals?.tickets?.paid || 0);

  return (
    <section className="space-y-6" data-testid="staff-referrals">
      <HowItWorks topics={["link", "money"]} />
      <div>
        <h2 className="font-display uppercase text-3xl tracking-tight">My Referral Link</h2>
        <p className="text-sm text-[var(--trio-muted)] mt-1">
          Any customer who books a table or buys a ticket through your link earns you a commission.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3" data-testid="staff-comm-totals">
        <StatCard label="Pending" value={`$${totalPending.toFixed(2)}`} accent="var(--trio-muted)" testid="staff-comm-pending" />
        <StatCard label="Earned" value={`$${totalEarned.toFixed(2)}`} accent="var(--trio-accent)" testid="staff-comm-earned" />
        <StatCard label="Paid Out" value={`$${totalPaid.toFixed(2)}`} accent="var(--trio-primary)" testid="staff-comm-paid" />
      </div>

      <div className="border border-[var(--trio-border)] bg-[var(--trio-surface)] p-5 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start">
        <div data-testid="staff-qr" className="p-3 bg-white border border-black/10">
          <QRCodeCanvas value={link} size={168} level="M" includeMargin={false} />
        </div>

        <div className="space-y-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-muted)] font-bold">Your short link</div>
            <div className="flex items-center gap-2 mt-1">
              <input
                readOnly
                value={link}
                data-testid="staff-ref-link"
                className="flex-1 bg-white border border-[var(--trio-border)] px-3 py-2 text-sm font-mono"
              />
              <button
                onClick={() => copy(link, "Link")}
                data-testid="staff-copy-link"
                className="p-2 border border-[var(--trio-border)] hover:border-[var(--trio-primary)]"
                title="Copy"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={shareLink}
              data-testid="staff-share-link"
              className="inline-flex items-center gap-2 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white px-4 py-2 text-xs tracking-widest uppercase font-bold"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
            <button
              onClick={downloadQR}
              data-testid="staff-download-qr"
              className="inline-flex items-center gap-2 border border-[var(--trio-text)] hover:bg-[var(--trio-text)] hover:text-[var(--trio-bg)] px-4 py-2 text-xs tracking-widest uppercase font-bold"
            >
              <Download className="w-4 h-4" /> Download QR
            </button>
          </div>

          <div className="pt-3 border-t border-[var(--trio-border)] space-y-2">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-muted)] font-bold">Direct-to links</div>
            <ShortLinkRow label="Book a table" url={bookingLink} onCopy={() => copy(bookingLink, "Booking link")} />
            <ShortLinkRow label="Buy event tickets" url={eventsLink} onCopy={() => copy(eventsLink, "Events link")} />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="font-display uppercase text-xl tracking-tight">Recent Activity</h3>
        <CommissionsList reservations={comms?.reservations || []} tickets={comms?.tickets || []} />
      </div>
    </section>
  );
}

function StatCard({ label, value, accent, testid }) {
  return (
    <div className="border border-[var(--trio-border)] bg-[var(--trio-surface)] p-4" data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-muted)] font-bold">{label}</div>
      <div className="font-display text-3xl mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function ShortLinkRow({ label, url, onCopy }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 min-w-0">
        <div className="text-xs text-[var(--trio-muted)]">{label}</div>
        <div className="text-xs font-mono truncate">{url}</div>
      </div>
      <button
        onClick={onCopy}
        className="p-1.5 border border-[var(--trio-border)] hover:border-[var(--trio-primary)]"
        title="Copy"
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function CommissionsList({ reservations, tickets }) {
  const rows = [
    ...reservations.map((r) => ({ kind: "reservation", ...r })),
    ...tickets.map((t) => ({ kind: "ticket", ...t })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  if (rows.length === 0) {
    return (
      <div className="border border-dashed border-[var(--trio-border)] p-8 text-center text-sm text-[var(--trio-muted)]">
        No commissions yet. Share your link to start earning.
      </div>
    );
  }

  return (
    <ul className="divide-y divide-[var(--trio-border)] border border-[var(--trio-border)]" data-testid="staff-comm-list">
      {rows.slice(0, 25).map((r, i) => {
        const isTicket = r.kind === "ticket";
        const label = isTicket ? `Ticket · ${r.event_title}` : `Reservation · ${r.name}`;
        const sub = isTicket ? `${r.quantity} × $${(r.unit_price || 0).toFixed(2)}` : `${r.party_size} guests · ${r.date}`;
        const amt = Number(r.commission_amount || 0);
        const status = r.commission_status || "pending";
        const badgeColor = status === "earned" ? "bg-[var(--trio-accent)] text-black"
          : status === "paid" ? "bg-green-600 text-white"
          : status === "canceled" ? "bg-red-100 text-red-700 line-through"
          : "bg-[var(--trio-border)] text-[var(--trio-muted)]";
        return (
          <li key={`${r.kind}-${r.id || i}`} className="px-4 py-3 flex items-center justify-between gap-4" data-testid={`staff-comm-row-${i}`}>
            <div className="min-w-0 flex-1">
              <div className="font-medium text-sm truncate">{label}</div>
              <div className="text-xs text-[var(--trio-muted)]">{sub}</div>
            </div>
            <div className="text-right">
              <div className="font-display text-lg">${amt.toFixed(2)}</div>
              <div className={`inline-block text-[9px] tracking-widest uppercase px-1.5 py-0.5 mt-0.5 ${badgeColor}`}>
                {status}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ---------- Leaderboard ----------
function Leaderboard() {
  const [period, setPeriod] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hallOfFame, setHallOfFame] = useState([]);

  useEffect(() => {
    setLoading(true);
    adminApi
      .get(`/staff/leaderboard?period=${period}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period]);

  useEffect(() => {
    adminApi
      .get("/staff/hall-of-fame")
      .then((r) => setHallOfFame(r.data || []))
      .catch(() => setHallOfFame([]));
  }, []);

  if (loading) {
    return (
      <section className="flex items-center gap-2 text-sm text-[var(--trio-muted)]" data-testid="leaderboard-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading the rankings…
      </section>
    );
  }

  if (!data || !data.leaderboard?.length) {
    return (
      <section className="space-y-4" data-testid="leaderboard-empty">
        <h2 className="font-display uppercase text-3xl tracking-tight">Leaderboard</h2>
        <HowItWorks topics={["money", "leaderboard"]} />
        <div className="border border-dashed border-[var(--trio-border)] p-10 text-center text-sm text-[var(--trio-muted)]">
          Not enough referrals yet — be the first to make the board.
        </div>
      </section>
    );
  }

  const rows = data.leaderboard;
  const top3 = rows.slice(0, 3);
  const rest = rows.slice(3);
  const myRow = rows.find((r) => r.is_me);

  return (
    <section className="space-y-6" data-testid="leaderboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display uppercase text-3xl tracking-tight flex items-center gap-2">
            <Trophy className="w-7 h-7 text-[var(--trio-accent)]" />
            Leaderboard
          </h2>
          <p className="text-sm text-[var(--trio-muted)] mt-1">
            Top earners from referral links across the team.
          </p>
        </div>
        <PeriodToggle value={period} onChange={setPeriod} />
      </div>
      <HowItWorks topics={["money", "leaderboard"]} />

      {/* Your rank pill */}
      {myRow && (
        <div
          data-testid="leaderboard-my-rank"
          className="bg-[var(--trio-primary)] text-white px-5 py-4 flex items-center justify-between gap-4"
        >
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-70 font-bold">
              Your Rank
            </div>
            <div className="font-display text-4xl leading-none">
              #{myRow.rank}
              <span className="text-lg opacity-70 ml-1">/ {data.total_staff}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.3em] uppercase opacity-70 font-bold">
              Earned {periodLabel(period)}
            </div>
            <div className="font-display text-4xl leading-none">${myRow.total.toFixed(2)}</div>
            <div className="text-xs opacity-80 mt-1">{myRow.count} referral{myRow.count !== 1 ? "s" : ""}</div>
          </div>
        </div>
      )}

      {/* Podium — Top 3 */}
      {top3.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="leaderboard-podium">
          {/* Reorder to put #1 in the middle on desktop: 2, 1, 3 */}
          {podiumOrder(top3).map((r) => (
            <PodiumCard key={r.id} row={r} />
          ))}
        </div>
      )}

      {/* Rest of the pack */}
      {rest.length > 0 && (
        <div className="border border-[var(--trio-border)] bg-[var(--trio-surface)] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] tracking-widest uppercase text-[var(--trio-muted)] border-b border-[var(--trio-border)]">
                <th className="px-4 py-3 w-16 text-center">Rank</th>
                <th className="px-4 py-3">Staff</th>
                <th className="px-4 py-3 text-right hidden sm:table-cell">Referrals</th>
                <th className="px-4 py-3 text-right">Earned</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--trio-border)]">
              {rest.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`leaderboard-row-${r.rank}`}
                  className={r.is_me ? "bg-[var(--trio-accent)]/20" : ""}
                >
                  <td className="px-4 py-3 text-center font-display text-2xl text-[var(--trio-muted)]">
                    {r.rank}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">
                      {r.name}
                      {r.is_me && (
                        <span className="ml-2 text-[9px] tracking-widest uppercase bg-[var(--trio-primary)] text-white px-1.5 py-0.5">
                          You
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[var(--trio-muted)]">{r.position || "Staff"}</div>
                  </td>
                  <td className="px-4 py-3 text-right hidden sm:table-cell text-[var(--trio-muted)]">
                    {r.count}
                  </td>
                  <td className="px-4 py-3 text-right font-display text-xl">
                    ${r.total.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-[var(--trio-muted)] text-center italic">
        Rankings update as reservations get confirmed and tickets get sold. Only earned + paid commissions count. Board resets on the 1st of every month.
      </p>

      {/* Hall of Fame — past monthly winners */}
      {hallOfFame.filter((h) => h.winner).length > 0 && (
        <div className="pt-8 border-t border-[var(--trio-border)] space-y-4" data-testid="hall-of-fame">
          <div>
            <h3 className="font-display uppercase text-2xl tracking-tight flex items-center gap-2">
              <Crown className="w-6 h-6 text-[var(--trio-accent)]" />
              Hall of Fame
            </h3>
            <p className="text-xs text-[var(--trio-muted)] mt-1">Past monthly champs.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {hallOfFame
              .filter((h) => h.winner)
              .slice(0, 12)
              .map((h) => (
                <div
                  key={h.month}
                  data-testid={`hof-${h.month}`}
                  className="border border-[var(--trio-border)] bg-[var(--trio-surface)] p-4"
                >
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-muted)] font-bold">
                    {h.month_label}
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <Crown className="w-4 h-4 text-[var(--trio-accent)] flex-shrink-0" />
                    <div className="font-display text-xl leading-tight truncate">
                      {h.winner.name}
                    </div>
                  </div>
                  <div className="mt-1 font-display text-2xl text-[var(--trio-primary)]">
                    ${(h.winner.total || 0).toFixed(2)}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </section>
  );
}

function periodLabel(p) {
  return p === "week" ? "this week" : p === "month" ? "this month" : "all-time";
}

function podiumOrder(top3) {
  if (top3.length < 3) return top3;
  return [top3[1], top3[0], top3[2]]; // 2, 1, 3
}

function PodiumCard({ row }) {
  const isFirst = row.rank === 1;
  const isSecond = row.rank === 2;
  const isThird = row.rank === 3;

  const Icon = isFirst ? Crown : Medal;
  const color = isFirst
    ? "var(--trio-accent)"
    : isSecond
    ? "#c0c0c0"
    : "#cd7f32";
  const border = isFirst ? "border-[var(--trio-accent)]" : "border-[var(--trio-border)]";
  const height = isFirst ? "sm:pt-10 sm:pb-8" : "sm:pt-6 sm:pb-6";

  return (
    <div
      data-testid={`podium-${row.rank}`}
      className={`relative border ${border} bg-[var(--trio-surface)] px-5 py-6 ${height} text-center ${
        isFirst ? "sm:-translate-y-2 sm:shadow-[0_0_0_2px_var(--trio-accent)]" : ""
      } ${row.is_me ? "ring-2 ring-[var(--trio-primary)]" : ""}`}
    >
      <div
        className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full p-2"
        style={{ background: color }}
      >
        <Icon className="w-5 h-5 text-black" />
      </div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-muted)] font-bold mt-2">
        {isFirst ? "Top Earner" : isSecond ? "Runner-up" : "3rd Place"}
      </div>
      <div className="font-display text-2xl tracking-tight mt-2 leading-tight">
        {row.name}
        {row.is_me && (
          <span className="ml-2 text-[9px] tracking-widest uppercase bg-[var(--trio-primary)] text-white px-1.5 py-0.5 align-middle">
            You
          </span>
        )}
      </div>
      <div className="text-xs text-[var(--trio-muted)] mt-0.5">{row.position || "Staff"}</div>
      <div
        className="font-display mt-4"
        style={{ fontSize: isFirst ? "3rem" : "2.25rem", color: "var(--trio-primary)", lineHeight: 1 }}
      >
        ${row.total.toFixed(2)}
      </div>
      <div className="text-xs text-[var(--trio-muted)] mt-1">
        {row.count} referral{row.count !== 1 ? "s" : ""}
      </div>
    </div>
  );
}

function PeriodToggle({ value, onChange }) {
  const opts = [
    { id: "week", label: "This Week" },
    { id: "month", label: "This Month" },
    { id: "all", label: "All-time" },
  ];
  return (
    <div className="inline-flex border border-[var(--trio-border)] bg-[var(--trio-surface)]" data-testid="leaderboard-period">
      {opts.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          data-testid={`leaderboard-period-${o.id}`}
          className={`px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold transition-colors ${
            value === o.id
              ? "bg-[var(--trio-text)] text-[var(--trio-bg)]"
              : "text-[var(--trio-muted)] hover:text-[var(--trio-text)]"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}


// ---------- Daily Summary Banner (dismissible, once per day) ----------
function DailySummaryBanner() {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const todayKey = new Date().toISOString().slice(0, 10);
  const storageKey = `trio_daily_dismissed_${todayKey}`;

  useEffect(() => {
    // Check if already dismissed today
    if (localStorage.getItem(storageKey)) {
      setDismissed(true);
      return;
    }
    adminApi.get("/staff/daily-summary").then((r) => setData(r.data)).catch(() => setData(null));
  }, [storageKey]);

  if (dismissed || !data) return null;

  const tone = {
    hot: {
      bg: "bg-[var(--trio-primary)]",
      text: "text-white",
      accent: "text-[var(--trio-accent)]",
      icon: <TrendingUp className="w-6 h-6" />,
    },
    warm: {
      bg: "bg-[var(--trio-accent)]",
      text: "text-black",
      accent: "text-[var(--trio-primary)]",
      icon: <Sparkles className="w-6 h-6" />,
    },
    steady: {
      bg: "bg-[var(--trio-surface)] border border-[var(--trio-border)]",
      text: "text-[var(--trio-text)]",
      accent: "text-[var(--trio-primary)]",
      icon: <TrendingUp className="w-6 h-6 text-[var(--trio-primary)]" />,
    },
    cold: {
      bg: "bg-[var(--trio-surface)] border border-dashed border-[var(--trio-border)]",
      text: "text-[var(--trio-text)]",
      accent: "text-[var(--trio-primary)]",
      icon: <Sparkles className="w-6 h-6 text-[var(--trio-muted)]" />,
    },
  }[data.mood] || {
    bg: "bg-[var(--trio-surface)]",
    text: "text-[var(--trio-text)]",
    accent: "text-[var(--trio-primary)]",
    icon: null,
  };

  function dismiss() {
    localStorage.setItem(storageKey, "1");
    setDismissed(true);
  }

  return (
    <div
      className={`${tone.bg} ${tone.text} px-4 py-3 mb-6 flex items-center gap-3 relative`}
      data-testid="daily-summary-banner"
      data-mood={data.mood}
    >
      <div className="flex-shrink-0">{tone.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm sm:text-base font-semibold" data-testid="daily-summary-headline">
          {data.headline}
        </div>
        <div className="text-xs opacity-90 mt-0.5" data-testid="daily-summary-subline">
          {data.subline}
        </div>
      </div>
      <button
        onClick={dismiss}
        data-testid="daily-summary-dismiss"
        className="flex-shrink-0 opacity-70 hover:opacity-100 p-1"
        title="Dismiss until tomorrow"
      >
        <CloseX className="w-4 h-4" />
      </button>
    </div>
  );
}

